import { setGraphqlClientCommonOptions } from '@sceneio/graphql-queries/src'
import { useCallback, useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import {
  useEmailLogin,
  useGoogleLogin,
  useTokenLogin,
} from '../../hooks/auth/useAuth'
import { removeQueryParamsFromUrl } from '../../hooks/layout/useLayoutInitializer'
import {
  GoogleOAuthProvider,
  GoogleLogin,
  CredentialResponse,
} from '@react-oauth/google'
import { useForceReloadNotification, useRouter } from '@sceneio/cms-hooks'
import { appConfig } from '../../config/appConfig'
import '@sceneio/config-tailwind/styles.css'
import { Icon } from '@sceneio/ui-icons'
import { CanvasUIBackground } from './components/CanvasUIBackground'
import clsx from 'clsx'
import { dispatchType } from 'src/store/storeInstance'
import { USER } from 'src/store/redux/constatnts'
import { resolveBypassHeader } from '../../helpers/resolveBypassHeader'
import { PhosphorIconsScript } from '../../externals/PhosphorIconsScript'

const googleClientId = appConfig.service.google.recaptchaClientId

type LoginStepsType = 'login' | 'confirmation'

export default function LoginPage() {
  const queryParamsObj = Object.fromEntries(
    new URLSearchParams(window.location.search),
  )

  const router = useRouter()

  const [isAuthParsing, setIsAuthParsing] = useState('auth' in queryParamsObj)
  const [tokenError, setTokenError] = useState('')
  const [googleError, setGoogleError] = useState('')
  const [email, setEmail] = useState('')
  const [isDirty, setIsDirty] = useState(false)
  const [step, setStep] = useState<LoginStepsType>('login')
  const accessProjectUrl = 'projectName' in queryParamsObj
  const projectName = queryParamsObj?.projectName

  const sendEmailLogin = useEmailLogin()
  const sendGoogleLogin = useGoogleLogin()
  const sendTokenLogin = useTokenLogin()

  const recaptchaRef = useRef<any>()

  const forceReloadNotification = useForceReloadNotification({})

  setGraphqlClientCommonOptions({
    headers: resolveBypassHeader(),
    onQueryHashError() {
      forceReloadNotification()
    },
  })

  // check magic link if defined
  useEffect(() => {
    async function checkMagicLink() {
      if (!isAuthParsing) return

      sendTokenLogin({
        token: queryParamsObj!.auth,
        urlParams: queryParamsObj,
      }).then(({ data, error: magicTokenError }: any) => {
        if (!magicTokenError) {
          const {
            project: projectId,
            content: contentId,
            path: pathId,
            commentId,
            showPlans,
          } = queryParamsObj

          if (data?.isFirstLogin) {
            redirectToCanvasOnFirstLogin()
            return
          }

          removeQueryParamsFromUrl()

          if (projectId && contentId && pathId) {
            router.pushRoute(
              'liveEditorContent',
              {
                projectId,
                pathId,
                contentId,
              },
              {
                commentId,
              },
            )
            return
          }

          if (projectId) {
            router.pushRoute(
              'content',
              {
                projectId,
              },
              { showPlans },
            )
            return
          }

          return
        }

        setTokenError(
          `Your login link has expired.</br> Please log back in again.`,
        )
        setIsAuthParsing(false)
        // TODO: handle params (changed from hash to browser router)
        removeQueryParamsFromUrl()
      })
    }

    checkMagicLink()
  }, [])

  const handleFormChange = useCallback(() => {
    setTokenError('')
    setGoogleError('')
    setIsDirty(true)
  }, [])

  // new users should land directly on untitled project canvas after sign up
  const redirectToCanvasOnFirstLogin = () => {
    const {
      project: projectId,
      content: contentId,
      path: pathId,
    } = queryParamsObj

    if (projectId || contentId || pathId) return

    router.setQueryParams({
      ...queryParamsObj,
      redirectOnFirstLogin: true,
    })
  }

  const handleFormSubmit = async (event: any) => {
    event.preventDefault()
    const { email } = event.target
    setEmail(email.value)

    recaptchaRef.current.reset()
    const token = await recaptchaRef.current.executeAsync()

    if (!token) {
      console.warn('Re-Captcha token failed')
      return
    }

    sendEmailLogin({
      email: email.value,
      token,
      urlParams: queryParamsObj,
    }).then(() => {
      setTokenError('')
      setGoogleError('')
      setIsDirty(false)
      setStep('confirmation')
    })
  }

  const handleGoogleSuccessLogin = (response: CredentialResponse) => {
    const { credential } = response || {}

    sendGoogleLogin({
      token: credential ?? '',
      urlParams: queryParamsObj,
    }).then(({ data, error: resError }: any) => {
      if (resError) {
        setGoogleError('Google Login Failed')
        setTokenError('')
        return
      }
      if (data?.isFirstLogin) {
        redirectToCanvasOnFirstLogin()
      }
    })
  }

  function renderLoginForm() {
    return (
      <div className="tw-animate-fadeIn">
        <div className="tw-px-6 lg:tw-px-8 tw-py-8 tw-flex tw-flex-col tw-items-center">
          {projectName && (
            <h1 className="tw-text-[28px] tw-mb-8 tw-text-gray-10 tw-font-medium">
              <span>
                Access <b>{projectName}</b> in Scene.io
              </span>
            </h1>
          )}
          <div className="tw-max-h-[40px]">
            <GoogleOAuthProvider clientId={googleClientId}>
              <GoogleLogin
                containerProps={{
                  style: { display: 'inline-flex' },
                }}
                text="continue_with"
                width="360"
                onSuccess={handleGoogleSuccessLogin}
                onError={() => {
                  console.log('Login Failed')
                }}
              />
            </GoogleOAuthProvider>
          </div>
          {tokenError && (
            <div
              dangerouslySetInnerHTML={{ __html: tokenError }}
              className="login__token-error tw-text-danger tw-text-sm tw-mt-3"
            />
          )}

          {googleError && (
            <div className="text-danger tw-text-danger tw-text-sm tw-mt-3">
              {googleError}
            </div>
          )}
        </div>

        <div className="tw-flex tw-items-center tw-justify-center">
          <div className="tw-flex-1 tw-h-px tw-bg-gray-80 tw-mr-3"></div>
          <span className="tw-text-gray-50 tw-text-sm">
            or continue with email
          </span>
          <div className="tw-flex-1 tw-h-px tw-bg-gray-80 tw-ml-3"></div>
        </div>

        <div className="tw-px-6 lg:tw-px-8 tw-py-8 tw-flex tw-flex-col tw-items-center">
          <form
            onSubmit={handleFormSubmit}
            onChange={handleFormChange}
            className="login__form tw-mb-2 tw-w-full tw-flex tw-flex-col tw-items-center"
          >
            <div className="tw-border tw-border-gray-80 tw-rounded-lg tw-mb-2 tw-flex tw-w-full md:tw-w-[360px]">
              <div className="tw-p-3.5 tw-border-r tw-flex tw-items-center tw-justify-center">
                <Icon
                  provider="phosphor"
                  className="tw-text-gray-10"
                  size="20px"
                  icon="At"
                />
              </div>
              <input
                name="email"
                type="email"
                className="tw-leading-5 tw-w-full tw-bg-transparent tw-px-3 tw-py-3.5 focus-visible:tw-outline-none placeholder:tw-text-gray-50 tw-text-gray-10 md:tw-text-sm"
                placeholder="Email"
                required
              />
            </div>

            <div className="tw-mb-2 tw-invisible">
              <ReCAPTCHA
                sitekey="6LfRKOEcAAAAAE2EahhVMVMQ8Sl93-5kvKFUmIvK"
                ref={recaptchaRef}
                size="invisible"
              />
            </div>

            {isDirty && (
              <button
                type="submit"
                className="tw-block tw-w-full tw-bg-primary hover:tw-bg-primary-hover tw-text-white tw-rounded-lg tw-px-6 tw-py-3.5 tw-text-sm tw-animate-fadeIn"
                disabled={isAuthParsing}
              >
                Continue with email
              </button>
            )}
          </form>
          {isDirty && (
            <div className="tw-flex tw-items-start tw-bg-gray-90 tw-p-3.5 tw-rounded-lg tw-animate-fadeIn">
              <Icon
                provider="phosphor"
                className="tw-text-gray-10 tw-mr-3.5"
                size="20px"
                icon="PaperPlaneTilt"
              />
              <p className="tw-text-start tw-text-sm tw-text-gray-10">
                We’ll send you a magic link so you can sign in without a
                password.
              </p>
            </div>
          )}
        </div>
        {!accessProjectUrl && renderPrivacyPolicy()}
      </div>
    )
  }

  function renderConfirmation() {
    return (
      <div className="tw-animate-fadeIn tw-flex tw-flex-col tw-items-center">
        <div className="tw-flex tw-justify-center tw-items-center tw-w-[56px] tw-h-[56px] tw-rounded-full tw-bg-green-90 tw-mt-8">
          <Icon
            provider="phosphor"
            className="tw-text-green-50"
            size="28px"
            icon="PaperPlaneTilt"
          />
        </div>
        <div className="tw-px-6 md:tw-px-[56px] tw-py-4 tw-text-center">
          <h1 className="tw-text-[24px] tw-mb-4 tw-text-gray-10 tw-font-normal">
            Check your email
          </h1>
          <p className="tw-text-gray-10 tw-text-sm">
            We’ve sent you a magic link to <strong>{email}</strong>. Please
            click the link to continue.
          </p>
        </div>
        <p className="tw-px-6 md:tw-px-[56px] tw-py-2 tw-text-sm tw-text-gray-50 tw-text-center">
          If you can’t find the email, please check your spam folder.
        </p>
        <div className="tw-h-px tw-bg-gray-80 tw-my-4 tw-w-full"></div>
        {renderBackToLogin()}
      </div>
    )
  }

  function renderBackToLogin() {
    return (
      <div className="tw-px-6 lg:tw-px-8 tw-text-center tw-mb-auto tw-pb-10">
        <p className="tw-text-sm tw-mb-4 tw-text-gray-50">
          Wrong email address?
        </p>
        <div>
          <button
            onClick={() => setStep('login')}
            className="tw-block tw-w-full tw-bg-white tw-text-primary tw-border tw-border-gray-80 tw-rounded-lg tw-px-6 tw-py-3.5 tw-text-sm hover:tw-bg-gray-4"
          >
            Sign in with a different email
          </button>
        </div>
      </div>
    )
  }

  function renderPrivacyPolicy() {
    return (
      <p className="tw-animate-fadeIn tw-text-sm tw-text-gray-50 tw-text-center tw-mt-auto tw-px-6 lg:tw-px-8 tw-pb-10">
        By clicking “Continue with Google” or “Continue with email”, you agree
        with Scene’s{' '}
        <a
          target="_blank"
          href="https://www.scene.io/privacy-policy"
          className="tw-text-primary"
        >
          Terms & Privacy
        </a>
        .
      </p>
    )
  }

  function renderSpinner() {
    return (
      <div className="tw-animate-fadeIn">
        <div className="tw-px-10 tw-pt-12 tw-pb-8">
          <h1 className="tw-text-[28px] tw-mb-8 tw-text-gray-10 tw-font-medium">
            Welcome to Scene
          </h1>
          <div>
            <div className="tw-w-[15px] tw-h-[15px] tw-border-2 tw-border-primary tw-border-b-transparent tw-rounded-full tw-animate-spin tw-mx-auto"></div>
          </div>
        </div>
      </div>
    )
  }

  function renderIntro() {
    return (
      <>
        {!accessProjectUrl && (
          <div
            className={clsx(
              'tw-animate-fadeIn xl:tw-w-[40%] lg:tw-w-[50%] tw-order-last lg:tw-order-first lg:tw-min-h-[100dvh] tw-min-h-[300px] lg:tw-rounded-br-[80px] lg:tw-rounded-tl-none tw-rounded-tl-[80px] tw-relative',
              {
                'tw-hidden': accessProjectUrl,
              },
            )}
            style={{
              backgroundImage: `url(https://assets.scene.io/global/brand/2025/login-page-background.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <h1
              style={{ fontFamily: 'Jokker' }}
              className="tw-absolute tw-min-w-[90%] sm:tw-min-w-[400px] tw-text-center lg:tw-text-left tw-top-[50%] tw-left-[50%] tw-translate-x-[-50%] tw-translate-y-[-50%] lg:tw-top-[20%] lg:tw-left-[40%] lg:tw-translate-x-[-40%] lg:tw-translate-y-0 tw-text-white tw-mb-0 tw-text-2xl md:tw-text-[28px] lg:tw-text-[32px] 2xl:tw-text-4xl tw-font-medium"
            >
              The smart way
              <br />
              to build websites.
            </h1>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <script
        src="https://projects.google.com/gsi/client"
        id="gsi-client"
        async
        defer
      />
      <PhosphorIconsScript />
      <div className="tw-relative tw-bg-white lg:tw-h-[100dvh] tw-min-h-[100dvh] tw-flex tw-flex-col">
        {accessProjectUrl && <CanvasUIBackground />}
        <div className="tw-flex tw-flex-col lg:tw-flex-row tw-grow">
          {renderIntro()}
          <div className="tw-animate-fadeIn tw-grow tw-flex tw-flex-col tw-justify-center tw-items-center tw-z-20 tw-overflow-hidden">
            <div
              className={clsx(
                'tw-flex tw-flex-col tw-justify-center tw-items-center tw-max-w-[424px] tw-py-10',
                {
                  'tw-bg-white tw-rounded-xl tw-text-center md:tw-border md:tw-border-gray-80 md:tw-shadow-large':
                    accessProjectUrl,
                },
              )}
            >
              {step === 'login' && (
                <img
                  src="https://assets.scene.io/global/brand/2025/logo-2025.png"
                  className="tw-w-[136px]"
                  alt="Scene.io"
                />
              )}
              {isAuthParsing && renderSpinner()}
              {step === 'login' && !isAuthParsing && renderLoginForm()}
              {step === 'confirmation' && renderConfirmation()}
              {!accessProjectUrl && (
                <div className="tw-flex tw-items-center tw-gap-4 tw-justify-center tw-flex-wrap tw-px-6 lg:tw-px-0">
                  <img
                    src="https://assets.scene.io/global/placeholders/avatars.svg"
                    width={168}
                  />
                  <span className="tw-text-sm tw-font-medium">
                    Join 15,000+ web professionals
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
